import { BillingAmountUpdateComponent } from '../billing-amount-update/billing-amount-update.component';
import { Component, OnInit, ViewEncapsulation, ViewChild, AfterViewInit } from '@angular/core';
import { RevenueItemService } from '../shared/revenue-items/revenue-item.service';
import { InvoiceService } from '../shared/invoice/invoice.service';
import { InvoiceGenerationParamsDto } from '../shared/invoice/invoice-generation-params.dto';
import { PagedSearch, TranslationService, DatagridService, NotificationService } from 'src/app/shared';
import { BillingSearch } from '../shared/billing/billing-search.model';
import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { InvoiceGenerationPreviewDto } from '../shared/invoice/invoice-generation-preview.dto';
import { PagedResult } from 'src/app/shared/model/generic-paged-result.model';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { UserService } from 'src/app/users/shared/user.service';
import { formatDate } from '@angular/common';
import { debounce } from 'lodash-decorators';

@Component({
  selector: 'wl-invoice-generator',
  templateUrl: './invoice-generator.component.html',
  styleUrls: ['./invoice-generator.component.less'],
  encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class InvoiceGeneratorComponent implements OnInit, AfterViewInit {
  @ViewChild(BillingAmountUpdateComponent) amountUpdate: BillingAmountUpdateComponent;
  public activeRevenueItems = [];
  public revItemSearchResult = [];
  // any for now next iteration will seperate out the preview into two calls
  public billingPreview: PagedResult<Array<InvoiceGenerationPreviewDto>> = new PagedResult<Array<InvoiceGenerationPreviewDto>>();
  public rejectionPreview: PagedResult<Array<InvoiceGenerationPreviewDto>> = new PagedResult<Array<InvoiceGenerationPreviewDto>>();
  public allMonths = [];
  public invoiceGenerationParams: InvoiceGenerationParamsDto = {
    BillingSearchOptions: { PageNumber: 1, PageSize: 20 } as BillingSearch,
    RejectedSearchOptions: { PageNumber: 1, PageSize: 20 } as PagedSearch,
  } as InvoiceGenerationParamsDto;
  public selectedRevenueItems = [];
  public selectedBillingIds = [];
  public selectedRejectedIds = [];
  public actionsText: string;
  public previewActionOptions: MenuItem[];
  public rejectionActionOptions: MenuItem[];
  public selectAllPreviews: boolean;
  public selectAllRejections: boolean;
  public selectedMonth: any = {};
  public hasResults = false;
  public generateClicked = false;
  public previewSearching = false;
  public rejectionPreviewSearching = false;
  public showIncreaseModal = false;
  public showDeleteBatchModal = false;
  public showDelete = false;
  public numberOfBatches: number;
  public deleting = false;
  public activeTabIndex: string = '0';
  public userAccessLevelAdmin: number;
  public hideBatchDelete: boolean;

  private _holdSearch = true;
  isExporting: boolean = false;

  constructor(
    private _datagridService: DatagridService,
    private _invoiceService: InvoiceService,
    private _navigationService: NavigationService,
    private _notificationService: NotificationService,
    private _revenueItemService: RevenueItemService,
    private _translationService: TranslationService,
    private _userService: UserService
  ) {
    const today = new Date();
    this.invoiceGenerationParams.BillingYear = today.getFullYear();
    this.invoiceGenerationParams.InvoiceDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
    this.numberOfBatches = 0;
  }

  ngOnInit() {
    this._revenueItemService.getRevenueItems().then((data) => {
      this.activeRevenueItems = data;
    });

    this.allMonths = this._translationService.getMonths().map((x, i) => {
      return { MonthName: x, MonthNum: ++i };
    });

    this.selectedMonth = this.allMonths.find((x) => x.MonthNum == new Date().getMonth() + 1);

    this._userService.getCurrentUser().then((data) => {
      this.invoiceGenerationParams.NotificationEmail = data.Email;
    });

    this._userService.getCurrentUserSecurityElementAccessLevel('Admin_Module').then((data: number) => {
      this.userAccessLevelAdmin = data;
      if (this.userAccessLevelAdmin == 0 || this.userAccessLevelAdmin == 1) {
        this.hideBatchDelete = true;
      } else {
        this.hideBatchDelete = false;
      }
    });
    this.initActionsOptions();
  }

  ngAfterViewInit() {
    this.amountUpdate.cancelUpdate.subscribe(() => {
      this.showIncreaseModal = false;
      this.searchBillings(true);
    });
  }

  searchRevenueItems(event: any) {
    this.revItemSearchResult = this.activeRevenueItems.filter((x) => x.ItemName.toLowerCase().includes(event.query.toLowerCase()));
  }

  lazyLoadBillings(e: LazyLoadEvent) {
    if (!this._holdSearch && this.invoiceGenerationParams) {
      this._datagridService.mapDatagridPagingToPagedSearch(e, this.invoiceGenerationParams.BillingSearchOptions);
      this.searchPreview();
    }
  }

  @debounce(1000)
  lazyLoadRejections(e: LazyLoadEvent) {
    if (!this._holdSearch && this.invoiceGenerationParams != null) {
      this._datagridService.mapDatagridPagingToPagedSearch(e, this.invoiceGenerationParams.RejectedSearchOptions);
      this.searchRejections();
    }
  }

  /**
   * run the search
   * @param clear clear the selections the user has made and rerun the search
   */
  searchBillings(clear: boolean) {
    if (clear) {
      this.selectedBillingIds = [];
      this.selectedRejectedIds = [];
      this.invoiceGenerationParams.IncludeBillings = [];
      this.invoiceGenerationParams.ExcludeBillings = [];
      this.invoiceGenerationParams.ExcludeAll = false;
      this.invoiceGenerationParams.IncludeAll = false;
      this.invoiceGenerationParams.BillingSearchOptions.PageNumber = 1;
      this.invoiceGenerationParams.RejectedSearchOptions.PageNumber = 1;
    }
    this.invoiceGenerationParams.AnchorMonth = this.selectedMonth.MonthNum;
    this.searchPreview();
    this.searchRejections();
  }

  searchPreview() {
    this.previewSearching = true;
    this.invoiceGenerationParams.BillingSearchOptions.RevenueItemNums = this.selectedRevenueItems.map((x) => x.ItemNum);
    this._invoiceService
      .GetInvoiceGenerationPreview(this.invoiceGenerationParams)
      .then((data) => {
        data.Result.forEach((item) => {
          item.Selected = this.selectedBillingIds.includes(item.BillingId);
        });
        this.billingPreview = data;
        this.invoiceGenerationParams.BillingSearchOptions.TotalCount = data.TotalCount;
        this.invoiceGenerationParams.BillingSearchOptions.TotalPages = data.TotalPages;
        this._holdSearch = false;
        this.hasResults =
          this.invoiceGenerationParams.BillingSearchOptions.TotalCount + this.invoiceGenerationParams.RejectedSearchOptions.TotalPages > 0;
      })
      .finally(() => {
        this.previewSearching = false;
        this.generateClicked = false;
      });
  }

  searchRejections() {
    this.rejectionPreviewSearching = true;
    this._invoiceService
      .GetInvoiceGenerationPreviewRejections(this.invoiceGenerationParams)
      .then((data) => {
        data.Result.forEach((item) => {
          item.Selected = this.selectedRejectedIds.includes(item.BillingId);
        });
        this.rejectionPreview = data;
        this.invoiceGenerationParams.RejectedSearchOptions.TotalCount = data.TotalCount;
        this.invoiceGenerationParams.RejectedSearchOptions.TotalPages = data.TotalPages;
        this.hasResults =
          this.invoiceGenerationParams.BillingSearchOptions.TotalCount + this.invoiceGenerationParams.RejectedSearchOptions.TotalPages > 0;
        this.rejectionPreviewSearching = false;
      })
      .finally(() => {
        this.rejectionPreviewSearching = false;
      });
  }

  /**
   * generate the billings
   */
  async generateBillings() {
    // don't run generation till a search has been run
    if (!this._holdSearch) {
      this.generateClicked = true;
      const invoiceDateString = formatDate(this.invoiceGenerationParams.InvoiceDate, 'yyyy-MM-dd 12:00', 'en-US');
      this.invoiceGenerationParams.InvoiceDate = new Date(invoiceDateString);
      let message: string = await this._invoiceService.GenerateInvoices(this.invoiceGenerationParams);
      this._notificationService.info(message);
    }
  }

  /**
   * opens the invoices batch report
   */
  viewInvoices() {
    this._navigationService.navigateToInvoiceBatchCreationReport();
  }

  /**
   * select or unselect all
   */
  onSelectAllRejectionChange(value: boolean) {
    this.selectedRejectedIds = [];
    this.rejectionPreview.Result.forEach((x) => (x.Selected = value));
  }

  /**
   * select or unselect all
   */
  onSelectAllPreviewChange(value: boolean) {
    this.selectedBillingIds = [];
    this.billingPreview.Result.forEach((x) => (x.Selected = value));
  }

  /**
   * remove or add the preview rows from the selected id list as necessary
   */
  onPreviewSelectedChanged(preview: InvoiceGenerationPreviewDto) {
    if (preview.Selected != this.selectAllPreviews) {
      this.selectedBillingIds.push(preview.BillingId);
    } else {
      const start = this.selectedBillingIds.findIndex((x) => x == preview.BillingId);
      if (start >= 0) {
        this.selectedBillingIds.splice(start, 1);
      }
    }
  }

  /**
   * remove or add the rejected rows from the selected id list as necessary
   */
  onRejectedSelectedChanged(billing: InvoiceGenerationPreviewDto) {
    if (billing.Selected != this.selectAllRejections) {
      this.selectedRejectedIds.push(billing.BillingId);
    } else {
      const start = this.selectedRejectedIds.findIndex((x) => x == billing.BillingId);
      if (start >= 0) {
        this.selectedRejectedIds.splice(start, 1);
      }
    }
  }

  /**
   * init the actions menus
   */
  private initActionsOptions() {
    const p1 = this._translationService.get('revenue.invoice-generator.actions.remove-label');
    const p2 = this._translationService.get('revenue.invoice-generator.actions.override-label');
    const p3 = this._translationService.get('revenue.invoice-generator.actions.export-label');

    Promise.all([p1, p2, p3]).then((data: string[]) => {
      this.previewActionOptions = [
        {
          label: data[0].toUpperCase(),
          command: (event) => {
            this.removeSelected();
          },
        },
        {
          label: data[2].toUpperCase(),
          command: () => {
            this.exportPreviewToCsv();
          },
        },
      ];
      this.rejectionActionOptions = [
        {
          label: data[1].toUpperCase(),
          command: (event) => {
            this.overrideSelected();
          },
        },
        {
          label: data[2].toUpperCase(),
          command: () => {
            this.exportRejectedToCsv();
          },
        },
      ];
    });
  }

  /**
   * force the rows from the rejections grid the user has selected to be generated
   */
  overrideSelected() {
    if (this.selectAllRejections) {
      // include and exclude all are mutually exclusive
      this.invoiceGenerationParams.IncludeAll = true;
      this.invoiceGenerationParams.ExcludeAll = false;
      this.invoiceGenerationParams.ExcludeBillings = [];
    }

    // make sure the excluded and included don't have the same ids in the list
    if (!this.invoiceGenerationParams.IncludeAll) {
      this.invoiceGenerationParams.IncludeBillings.push(...this.selectedRejectedIds);
      for (const id of this.selectedRejectedIds) {
        const start = this.invoiceGenerationParams.ExcludeBillings.findIndex((x) => x == id);
        if (start >= 0) {
          this.invoiceGenerationParams.ExcludeBillings.splice(start, 1);
        }
      }
    } else {
      if (this.selectAllRejections) {
        // include all really only cares about what's in the exclude list
        this.invoiceGenerationParams.ExcludeBillings.push(...this.selectedRejectedIds);
      } else {
        for (const id of this.selectedRejectedIds) {
          const start = this.invoiceGenerationParams.ExcludeBillings.findIndex((x) => x == id);
          if (start >= 0) {
            this.invoiceGenerationParams.ExcludeBillings.splice(start, 1);
          }
        }
      }
    }
    this.selectAllRejections = false;
    this.searchBillings(false);
    this.selectedBillingIds = [];
    this.selectedRejectedIds = [];
  }

  /**
   * Exports the rejections grid to csv and navigates the user to the file.
   */
  async exportRejectedToCsv() {
    if (this.isExporting) {
      return;
    }

    this.isExporting = true;

    try {
      const url: string = await this._invoiceService.ExportInvoiceGenerationRejectionPreview(this.invoiceGenerationParams);

      this._navigationService.popupSafeNavigate(url);
    } catch (e) {
    } finally {
      this.isExporting = false;
    }
  }

  /**
   * removes the previews the user has selected from the range of invoices that will be generated
   */

  removeSelected() {
    if (this.selectAllPreviews) {
      // include and exclude all are mutually exclusive
      this.invoiceGenerationParams.ExcludeAll = true;
      this.invoiceGenerationParams.IncludeAll = false;
      this.invoiceGenerationParams.IncludeBillings = [];
    }

    // make sure the excluded and included don't have the same ids in the list
    if (!this.invoiceGenerationParams.ExcludeAll) {
      this.invoiceGenerationParams.ExcludeBillings.push(...this.selectedBillingIds);
      for (const id of this.selectedBillingIds) {
        const start = this.invoiceGenerationParams.IncludeBillings.findIndex((x) => x == id);
        if (start >= 0) {
          this.invoiceGenerationParams.IncludeBillings.splice(start, 1);
        }
      }
    } else {
      if (this.selectAllPreviews) {
        this.invoiceGenerationParams.IncludeBillings.push(...this.selectedBillingIds);
      } else {
        for (const id of this.selectedBillingIds) {
          const start = this.invoiceGenerationParams.IncludeBillings.findIndex((x) => x == id);
          if (start >= 0) {
            this.invoiceGenerationParams.IncludeBillings.splice(start, 1);
          }
        }
      }
    }
    this.selectAllPreviews = false;
    this.searchBillings(false);
    this.selectedBillingIds = [];
    this.selectedRejectedIds = [];
    this.selectAllPreviews = false;
  }

  /**
   * Exports the preview grid to csv and navigates the user to the file.
   */
  async exportPreviewToCsv() {
    if (this.isExporting) {
      return;
    }

    this.isExporting = true;

    try {
      const url: string = await this._invoiceService.ExportInvoiceGenerationPreview(this.invoiceGenerationParams);

      this._navigationService.popupSafeNavigate(url);
    } catch (e) {
    } finally {
      this.isExporting = false;
    }
  }

  launchIncreaseModal() {
    this.amountUpdate.search();
    this.showIncreaseModal = true;
  }
  launchDeleteBatchModal() {
    this.showDeleteBatchModal = true;
  }
  close() {
    this.deleting = false;
    this.numberOfBatches = 0;
    this.showDelete = false;
    this.showDeleteBatchModal = false;
  }
  next(batchNum): void {
    this._invoiceService.getBatches(batchNum).then((data) => {
      if (data !== 0) {
        this.numberOfBatches = data;
        this.showDelete = true;
      }
    });
  }
  deleteBatch(batchNum) {
    this.deleting = true;
    this._invoiceService
      .deleteBatches(batchNum)
      .then(() => {
        var message = this._translationService.getInstant('revenue.invoice-generator.delete-success-message');
        this._notificationService.success(message);
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      })
      .finally(() => {
        this.close();
      });
  }

  rejectionPageSizeChanged() {
    this.invoiceGenerationParams.RejectedSearchOptions.PageNumber = 1;
    this.searchRejections();
  }

  billingPageSizeChanged() {
    this.invoiceGenerationParams.BillingSearchOptions.PageNumber = 1;
    this.searchPreview();
  }
}
